import type { HTMLProps, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';

import { ContainerContext } from '@playbook/modules/layout/container/container-context';
import { cn, cv } from '@playbook/utils';

import css from './styles.module.css';

/**
 *	Named sizes, ascending. `ContainerSizes[n]` is the size a container is at
 *	once it has cleared `ContainerBreakpoints[n - 1]`.
 *
 *	@note Container query conditions cannot read a custom property, so there is
 *	no shared source for these widths. They are repeated in the `@container`
 *	`@container` rules in the row and column stylesheets; changing a breakpoint
 *	means editing all three by hand. `Viewport` keeps its own pair.
 */
const ContainerBreakpoints: number[] = [600, 960] as const;

export type ContainerSize = 'small' | 'medium' | 'large';

const ContainerSizes: ContainerSize[] = ['small', 'medium', 'large'];

/**
 *	Every size the container has reached, ascending and space separated — a
 *	`large` container carries all three, a `medium` one carries `small medium`.
 *
 *	Cumulative rather than a single name so that matching one token with `~=`
 *	reads as "at or above this breakpoint", the way `min-width` does. Targeting
 *	one band exclusively is deliberately not expressible here; write a regular
 *	`@container` query for that.
 */
function resolveContainerSizes(width: number): string {
	let step = 0;

	for (const breakpoint of ContainerBreakpoints) {
		if (width >= breakpoint) {
			step += 1;
		}
	}

	return ContainerSizes.slice(0, step + 1).join(' ');
}

interface ContainerProps extends HTMLProps<HTMLDivElement> {
	name?: string;

	onLayoutChange?: () => void;

	className?: string;

	children?: ReactNode | ReactNode[];
}

export function Container({
	name,

	onLayoutChange,

	className,

	style,

	children,

	...props
}: ContainerProps): ReactNode {
	const ref = useRef<HTMLDivElement | null>(null);
	const onLayoutChangeRef = useRef(onLayoutChange);

	onLayoutChangeRef.current = onLayoutChange;

	useLayoutEffect(() => {
		const node = ref.current;

		if (!node) {
			return;
		}

		let currentSizes = resolveContainerSizes(node.clientWidth);

		node.dataset.containerSize = currentSizes;

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const nextSizes = resolveContainerSizes(entry.contentRect.width);

				if (nextSizes !== currentSizes) {
					currentSizes = nextSizes;
					node.dataset.containerSize = nextSizes;
					onLayoutChangeRef.current?.();
				}
			}
		});

		observer.observe(node);

		return () => observer.disconnect();
	}, []);

	return (
		<ContainerContext.Provider value={{ mode: 'container', name }}>
			<div
				{...props}
				ref={ref}
				className={cn(css.container, className)}
				style={{ ...style, ...cv({ containerName: name || 'layout' }) }}>
				{children}
			</div>
		</ContainerContext.Provider>
	);
}
